import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";

const SITE_URL = "https://www.mostrandomexico.mx";

export const Route = createFileRoute("/api-docs")({
  head: () => ({
    meta: [
      { title: "API pública — Mostrando México" },
      { name: "description", content: "Documentación de la API pública de Mostrando México para conectar tours, disponibilidad y reservas." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ApiDocsPage,
});

function Endpoint({
  method,
  path,
  desc,
  example,
  response,
}: {
  method: string;
  path: string;
  desc: string;
  example?: string;
  response: string;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">{method}</span>
        <code className="font-mono text-sm">{path}</code>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
      {example && (
        <pre className="mt-3 overflow-x-auto rounded-md bg-muted p-3 text-xs"><code>{example}</code></pre>
      )}
      <h4 className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Respuesta</h4>
      <pre className="mt-1 overflow-x-auto rounded-md bg-muted p-3 text-xs"><code>{response}</code></pre>
    </section>
  );
}

function ApiDocsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="font-display text-4xl font-bold">API pública v1 (canónica)</h1>
        <p className="mt-2 text-muted-foreground">
          API REST source-agnostic diseñada como capa canónica de Mostrando México. Sirve tanto los tours propios como los
          distribuidos vía Bokun bajo un único esquema estable, pensado para alimentar adaptadores a Civitatis, GetYourGuide,
          Viator, Booking.com y Expedia. Sin auth, respuestas JSON, CORS abierto, cache público 60 s, multilenguaje vía <code>?lang=</code>.
        </p>

        <div className="mt-3 flex flex-wrap gap-2 rounded-md bg-muted/40 p-3 text-xs">
          <span><strong>Base URL:</strong> <code>https://www.mostrandomexico.mx</code></span>
          <span>·</span>
          <a className="text-primary underline" href="/api/public/v1/docs">Docs interactivas (Redoc)</a>
          <span>·</span>
          <a className="text-primary underline" href="/api/public/v1/openapi.json">OpenAPI 3.1</a>
        </div>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/api/public/v1/tours"
            desc="Lista paginada del catálogo unificado (propios + Bokun). Filtros: city, state, country, category, language, source, min_price, max_price, min_duration, max_duration, q. Orden: featured|price_asc|price_desc|duration_asc|duration_desc|title_asc|title_desc. Paginación: page, page_size (máx 100). Idioma: lang=es|en|fr|it|pt."
            example={`curl 'https://www.mostrandomexico.mx/api/public/v1/tours?lang=en&city=cdmx&sort=price_asc&page=1&page_size=20'`}
            response={`{
  "data": [
    {
      "id": "mx-<uuid>",
      "source": "custom",
      "slug": "mx-tour-cdmx",
      "title": "CDMX walking tour",
      "summary": "...",
      "image": "https://...",
      "images": ["https://..."],
      "price_from": { "amount": 850, "currency": "MXN" },
      "duration_minutes": 240,
      "location": { "display": "CDMX, México", "city": "CDMX", "state": null, "country": "México", "latitude": null, "longitude": null },
      "categories": ["cultural"],
      "languages": ["es","en"],
      "featured": true,
      "available": true,
      "vendor": "Mostrando México",
      "rating": null,
      "review_count": null,
      "updated_at": null
    }
  ],
  "meta": { "total": 128, "page": 1, "page_size": 20, "total_pages": 7, "lang": "en", "sort": "price_asc", "filters": { "city": "cdmx" } }
}`}
          />

          <Endpoint
            method="GET"
            path="/api/public/v1/tours/:id"
            desc="Ficha canónica completa. `id` acepta mx-<uuid>, bkn-<numeric>, uuid crudo o id numérico Bokun. Incluye descripción, itinerario, meeting points, inclusiones/exclusiones, políticas, extras, precios por categoría, medios, idiomas del guía, ventana de reserva, restricciones y todo lo necesario para publicar en una OTA."
            example={`curl 'https://www.mostrandomexico.mx/api/public/v1/tours/bkn-995305?lang=fr'`}
            response={`{
  "data": {
    "id": "bkn-995305",
    "source": "bokun",
    "slug": "cenotes-riviera-maya-995305",
    "title": "Cenotes de la Riviera Maya",
    "description": "...",
    "highlights": ["..."],
    "included": ["..."],
    "excluded": ["..."],
    "important_info": "...",
    "cancellation_policy": "...",
    "meeting_points": [{ "id": "1", "name": "Muelle Playa del Carmen", "address": "...", "latitude": 20.6, "longitude": -87.07 }],
    "itinerary": [{ "order": 1, "title": "Salida", "description": "..." }],
    "start_times": [{ "hour": 9, "minute": 0, "label": "AM" }],
    "media": [{ "url": "https://...", "alt": null, "caption": null, "type": "image" }],
    "extras": [{ "id": "e1", "title": "Fotos submarinas", "price": { "amount": 300, "currency": "MXN" } }],
    "pricing": [{ "id": "adult", "label": "Adulto", "minAge": 12, "maxAge": null, "price": { "amount": 1200, "currency": "MXN" } }],
    "min_participants": 2, "max_participants": 12, "booking_cutoff_hours": 24, "instant_confirmation": true,
    "languages": ["es","en"], "categories": ["aventura","naturaleza"],
    "price_from": { "amount": 1200, "currency": "MXN" }
  },
  "meta": { "lang": "fr", "version": "v1" }
}`}
          />

          <Endpoint
            method="GET"
            path="/api/public/v1/tours/:id/availability"
            desc="Disponibilidad por fecha. Soporta tanto tours propios como Bokun. Query: from, to (YYYY-MM-DD)."
            example="curl 'https://www.mostrandomexico.mx/api/public/v1/tours/mx-UUID/availability?from=2026-06-15&to=2026-09-15'"
            response={`{
  "tour_id": "mx-<uuid>",
  "source": "custom",
  "from": "2026-06-15",
  "to": "2026-09-15",
  "slots": [
    { "id": "uuid", "date": "2026-06-20", "time": "09:00",
      "capacity": 12, "sold": 4, "available": 8, "status": "open" }
  ],
  "blocked": [{ "date": "2026-07-04", "reason": "Mantenimiento" }]
}`}
          />

          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-semibold">Errores</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Formato uniforme: <code>{`{ "error": { "code": "not_found", "message": "..." } }`}</code>. Códigos actuales:
              <code> invalid_id (400)</code>, <code>not_found (404)</code>, <code>upstream_error (502)</code>, <code>catalog_unavailable (502)</code>.
            </p>
          </section>

          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-semibold">Autenticación de partners</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Los endpoints de catálogo y disponibilidad son públicos y de solo lectura. Los endpoints de escritura
              (<code>POST /v1/bookings</code>) requieren el header <code>X-MM-Api-Key</code> (declarado como{" "}
              <code>partnerApiKey</code> en el OpenAPI), aplican rate limits por partner y soportan idempotencia vía{" "}
              <code>Idempotency-Key</code>. Los eventos de reserva se entregan por webhooks firmados con HMAC-SHA256.
              Gestiona tus llaves y webhooks en el Partner Portal (<code>/partner</code>).
            </p>
          </section>


          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-semibold">Conectores OTA</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Distribuimos en Bokun, Viator y GetYourGuide vía conectores nativos. Para integraciones
              personalizadas escríbenos a{" "}
              <a className="text-primary underline" href="mailto:api@mostrandomexico.com">api@mostrandomexico.com</a>.
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
